import { useState, useEffect, useCallback } from 'react';

interface Slide {
  title: string;
  subtitle: string;
  description: string;
  image?: string;
}

interface HeroCarouselProps {
  slides: Slide[];
  onQuoteOpen: () => void;
  onCallClick: () => void;
  ctaQuote: string;
  ctaCall: string;
  badges: { icon: string; text: string }[];
}

export default function HeroCarousel({
  slides,
  onQuoteOpen,
  onCallClick,
  ctaQuote,
  ctaCall,
  badges,
}: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (animating) return;
      setAnimating(true);
      setTimeout(() => {
        setCurrent(index);
        setAnimating(false);
      }, 300);
    },
    [animating]
  );

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, slides.length, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5500);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  const defaultBg = 'https://static.readdy.ai/image/44df2fb83f62b32263643d78e10a35f8/01f0b293a68253f006c6f18d6f7738e5.jfif';
  const bgImage = slide.image || defaultBg;

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background images — one per slide, crossfade */}
      {slides.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-700"
          style={{
            backgroundImage: `url('${s.image || defaultBg}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            opacity: i === current ? 1 : 0,
          }}
        />
      ))}

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/30 z-[1]"></div>

      <div className="container mx-auto px-6 relative z-10 w-full">
        <div className="max-w-4xl">
          {/* Slide Content */}
          <div
            className="transition-opacity duration-300"
            style={{ opacity: animating ? 0 : 1 }}
          >
            <p className="text-yellow-400 text-sm font-semibold uppercase tracking-widest mb-3">
              {slide.subtitle}
            </p>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-left text-white">
              {slide.title}
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-gray-200 max-w-2xl mb-10">
              {slide.description}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mb-10">
            <button
              onClick={onQuoteOpen}
              className="px-6 py-3 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-all duration-300 hover:scale-105 cursor-pointer flex items-center gap-2 text-sm whitespace-nowrap"
            >
              <i className="ri-send-plane-line"></i>
              {ctaQuote}
            </button>
            <button
              onClick={onCallClick}
              className="px-6 py-3 border border-gray-500 text-gray-200 font-semibold rounded-lg hover:border-yellow-400/60 hover:text-yellow-400 transition-all duration-300 cursor-pointer flex items-center gap-2 text-sm whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              {ctaCall}
            </button>
          </div>

          {/* Feature Badges */}
          <div className="flex flex-wrap gap-3 mb-12">
            {badges.map((badge) => (
              <div
                key={badge.text}
                className="bg-gradient-to-r from-yellow-400/20 to-yellow-500/20 border border-yellow-400/40 rounded-full px-4 py-2 backdrop-blur-sm"
              >
                <div className="flex items-center space-x-2">
                  <i className={`${badge.icon} text-yellow-400 text-sm`}></i>
                  <span className="text-yellow-100 font-semibold text-sm">{badge.text}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Slide Indicators */}
          <div className="flex items-center gap-3">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  i === current
                    ? 'w-8 h-2 bg-yellow-400'
                    : 'w-2 h-2 bg-gray-500 hover:bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Prev / Next arrows */}
      <button
        onClick={() => goTo((current - 1 + slides.length) % slides.length)}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-yellow-400/80 border border-gray-600 hover:border-yellow-400 rounded-full text-white hover:text-black transition-all duration-300 cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={() => goTo((current + 1) % slides.length)}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-yellow-400/80 border border-gray-600 hover:border-yellow-400 rounded-full text-white hover:text-black transition-all duration-300 cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <i className="ri-arrow-down-line text-2xl text-yellow-400"></i>
      </div>
    </section>
  );
}
